import React from 'react';
import cx from 'classnames';

import type {ButtonProps} from '@pexip/components';

import type {LeaveButtonDesktopProps} from './Controls/LeaveButton.view';
import {LeaveButtonDesktop} from './Controls/LeaveButton.view';
import type {VideoButtonProps} from './Controls/VideoButton.view';
import {VideoButton} from './Controls/VideoButton.view';
import type {AudioButtonProps} from './Controls/AudioButton.view';
import {AudioButton} from './Controls/AudioButton.view';
import type {DeviceStatusInfo} from '../../types';

import styles from './CallControls.module.scss';

export const CallControlsDesktopView: React.FC<
    React.ComponentProps<'div'> &
        LeaveButtonDesktopProps &
        VideoButtonProps &
        AudioButtonProps & {
            audioTooltipText?: AudioButtonProps['tooltipText'];
            canMuteAudio?: boolean;
            isAudioMuteDisabled?: boolean;
            isAudioSuspended: boolean;
            isRequestingAudio: boolean;
            isRequestingVideo: boolean;
            isVideoMuteDisabled?: boolean;
            isVideoSuspended?: boolean;
            isAudioError: boolean;
            isVideoError: boolean;
            isBothError: boolean;
            audioTooltipVisible: boolean;
            videoTooltipVisible: boolean;
            toggleAudioTooltip: () => void;
            toggleVideoTooltip: () => void;
            audioStatusInfo?: DeviceStatusInfo;
            videoStatusInfo?: DeviceStatusInfo;
        }
> = ({
    isAudioError,
    isVideoError,
    isBothError,
    audioTooltipVisible,
    videoTooltipVisible,
    toggleAudioTooltip,
    toggleVideoTooltip,
    audioStatusInfo,
    videoStatusInfo,
    audioTooltipText,
    canMuteAudio,
    iconSize,
    isAudioMuteDisabled,
    isAudioSuspended,
    isMicrophoneMuted,
    isRequestingAudio,
    isRequestingVideo,
    isVideoMuteDisabled,
    isVideoMuted,
    isVideoSuspended,
    onAudioBtnClick,
    onLeave,
    onVideoBtnClick,
    requestDeviceAgain,
    ...props
}) => {
    const buttonVariant: ButtonProps['variant'] = 'neutral';

    return (
        <div
            className={cx(styles.callControlsWrapper, styles.desktopWrapper)}
            {...props}
        >
            <div className={styles.desktopMediaBtnWrapper}>
                <AudioButton
                    canMute={canMuteAudio}
                    hasError={isAudioError || isBothError}
                    iconSize={iconSize}
                    inputStatusInfo={audioStatusInfo}
                    isDisabled={isAudioMuteDisabled}
                    isLoading={isRequestingAudio}
                    isMicrophoneMuted={isMicrophoneMuted}
                    isSuspended={isAudioSuspended}
                    onAudioBtnClick={
                        isBothError ? toggleVideoTooltip : onAudioBtnClick
                    }
                    requestDeviceAgain={requestDeviceAgain}
                    shouldShowTooltip={audioTooltipVisible}
                    toggleTooltip={toggleAudioTooltip}
                    tooltipClassName={styles.desktopAudioButtonTooltip}
                    variant={buttonVariant}
                />
                <VideoButton
                    hasError={isVideoError}
                    iconSize={iconSize}
                    inputStatusInfo={videoStatusInfo}
                    isDisabled={isVideoMuteDisabled}
                    isLoading={isRequestingVideo}
                    isSuspended={isVideoSuspended}
                    isVideoMuted={isVideoMuted}
                    onVideoBtnClick={onVideoBtnClick}
                    requestDeviceAgain={requestDeviceAgain}
                    shouldShowTooltip={videoTooltipVisible}
                    toggleTooltip={toggleVideoTooltip}
                    tooltipClassName={styles.desktopVideoButtonTooltip}
                    variant={buttonVariant}
                />
            </div>
            <div className={styles.desktopLeaveBtnWrapper}>
                <LeaveButtonDesktop onLeave={onLeave} />
            </div>
        </div>
    );
};

export type CallControlsDesktopProps = React.ComponentProps<
    typeof CallControlsDesktopView
>;
